import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';
import { toast } from '../components/Toast';
import DjamannLoader from '../components/DjamannLoader';
import { confirm } from '../components/ConfirmDialog';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';
import { ArrowLeft, Smartphone, Mail, MapPin, Printer, Edit, Send, CheckCircle, XCircle, FileText } from 'lucide-react';
import logger from '../utils/logger';

const BusinessQuoteView = () => {
  const routeBase = useWorkspaceBasePath();
  const { id } = useParams();
  const printRef = useRef();
  const [quote, setQuote] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    try {
      const [quoteRes, settingsRes] = await Promise.all([
        api.get(`/business/quotes/${id}`),
        api.get('/business/settings')
      ]);
      setQuote(quoteRes.data);
      setSettings(settingsRes.data);
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const originalContents = document.body.innerHTML;
    
    document.body.innerHTML = printContent.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const getStatusBadge = (statut) => {
    const styles = {
      brouillon: 'bg-gray-100 text-gray-600',
      envoye: 'bg-blue-100 text-blue-600',
      accepte: 'bg-green-100 text-green-600',
      refuse: 'bg-red-100 text-red-600',
      expire: 'bg-orange-100 text-orange-600'
    };
    const labels = {
      brouillon: 'Brouillon',
      envoye: 'Envoyé',
      accepte: 'Accepté',
      refuse: 'Refusé',
      expire: 'Expiré'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[statut] || styles.brouillon}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/business/quotes/${id}`, { statut: newStatus });
      loadData();
    } catch (err) {
      logger.error(err);
    }
  };

  const handleConvertToInvoice = async () => {
    if (!await confirm('Convertir ce devis en facture ?')) return;
    try {
      const res = await api.post(`/business/quotes/${id}/convert-to-invoice`);
      if (res.data.invoice_id) {
        window.location.href = `/workspace/invoices/${res.data.invoice_id}`;
      }
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de la conversion');
    }
  };

  if (loading) {
    return <DjamannLoader variant="section" />;
  }

  if (!quote) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600">Devis introuvable</p>
        <Link to={`${routeBase}/quotes`} className="text-indigo-600 hover:underline mt-2 block">
          Retour aux devis
        </Link>
      </div>
    );
  }

  const total = parseFloat(quote.total) || 0;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6 print:hidden">
        <div className="flex items-center gap-4">
          <Link to={`${routeBase}/quotes`} className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            <ArrowLeft size={16} className="inline" /> Retour
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{quote.numero_devis}</h1>
          </div>
        </div>
        {getStatusBadge(quote.statut)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Printable */}
        <div className="lg:col-span-2">
          <div ref={printRef}>
            {/* Print Styles */}
            <style>{`
              @media print {
                body { 
                  font-family: Arial, sans-serif;
                  -webkit-print-color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .print-container {
                  padding: 20px;
                }
                .no-print { display: none !important; }
              }
            `}</style>
            
            <div className="print-container bg-white rounded-xl shadow overflow-hidden">
              {/* Company Header */}
              <div className="p-8 border-b-4 border-indigo-600">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    {settings?.logo && (
                      <img 
                        src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`}
                        alt="Logo"
                        className="h-20 w-20 object-contain"
                      />
                    )}
                    <div>
                      <h1 className="text-2xl font-bold text-gray-800">
                        {settings?.nom_entreprise || 'Mon Entreprise'}
                      </h1>
                      {settings?.adresse && (
                        <p className="text-gray-600 text-sm">{settings.adresse}</p>
                      )}
                      {settings?.telephone && (
                        <p className="text-gray-600 text-sm"><Smartphone size={14} className="inline" /> {settings.telephone}</p>
                      )}
                      {settings?.email && (
                        <p className="text-gray-600 text-sm"><Mail size={14} className="inline" /> {settings.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-indigo-600">DEVIS</div>
                    <div className="text-lg font-medium text-gray-800 mt-1">{quote.numero_devis}</div>
                  </div>
                </div>
              </div>

              {/* Quote Info + Client */}
              <div className="p-8 grid grid-cols-2 gap-8 bg-gray-50">
                <div>
                  <div className="text-sm text-gray-500 uppercase tracking-wide mb-2">Destinataire</div>
                  <div className="bg-white rounded-lg p-4 border">
                    <div className="font-semibold text-gray-800 text-lg">{quote.client_nom || 'Client'}</div>
                    {quote.client_telephone && (
                      <div className="text-gray-600 text-sm mt-1"><Smartphone size={14} className="inline" /> {quote.client_telephone}</div>
                    )}
                    {quote.client_email && (
                      <div className="text-gray-600 text-sm"><Mail size={14} className="inline" /> {quote.client_email}</div>
                    )}
                    {quote.client_adresse && (
                      <div className="text-gray-600 text-sm"><MapPin size={14} className="inline" /> {quote.client_adresse}</div>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 uppercase tracking-wide mb-2">Informations</div>
                  <div className="bg-white rounded-lg p-4 border space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Date du devis:</span>
                      <span className="font-medium">{new Date(quote.date_devis).toLocaleDateString('fr-FR')}</span>
                    </div>
                    {quote.date_validite && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Valide jusqu'au:</span>
                        <span className="font-medium">{new Date(quote.date_validite).toLocaleDateString('fr-FR')}</span>
                      </div>
                    )}
                    {settings?.numero_registre && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">N° Registre:</span>
                        <span className="font-medium">{settings.numero_registre}</span>
                      </div>
                    )}
                    {settings?.numero_fiscal && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">N° Fiscal:</span>
                        <span className="font-medium">{settings.numero_fiscal}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Items Table */}
              <div className="p-8">
                <table className="w-full">
                  <thead>
                    <tr className="bg-indigo-600 text-white">
                      <th className="text-left py-3 px-4 rounded-tl-lg">Description</th>
                      <th className="text-center py-3 px-4">Quantité</th>
                      <th className="text-right py-3 px-4">Prix unitaire</th>
                      <th className="text-right py-3 px-4 rounded-tr-lg">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(quote.items || []).map((item, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="py-4 px-4 border-b">{item.description}</td>
                        <td className="py-4 px-4 text-center border-b">{item.quantite}</td>
                        <td className="py-4 px-4 text-right border-b">
                          {parseFloat(item.prix_unitaire).toLocaleString('fr-FR')} XOF
                        </td>
                        <td className="py-4 px-4 text-right border-b font-medium">
                          {(parseFloat(item.quantite) * parseFloat(item.prix_unitaire)).toLocaleString('fr-FR')} XOF
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totals */}
                <div className="mt-6 flex justify-end">
                  <div className="w-80">
                    {quote.sous_total && quote.sous_total !== quote.total && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600">Sous-total:</span>
                        <span className="font-medium">{parseFloat(quote.sous_total).toLocaleString('fr-FR')} XOF</span>
                      </div>
                    )}
                    {quote.remise > 0 && (
                      <div className="flex justify-between py-2 border-b text-red-600">
                        <span>Remise:</span>
                        <span>-{parseFloat(quote.remise).toLocaleString('fr-FR')} XOF</span>
                      </div>
                    )}
                    {quote.taxe > 0 && (
                      <div className="flex justify-between py-2 border-b">
                        <span className="text-gray-600">TVA:</span>
                        <span className="font-medium">{parseFloat(quote.taxe).toLocaleString('fr-FR')} XOF</span>
                      </div>
                    )}
                    <div className="flex justify-between py-4 bg-indigo-600 text-white rounded-lg px-4 mt-4">
                      <span className="font-bold text-lg">TOTAL</span>
                      <span className="font-bold text-lg">{total.toLocaleString('fr-FR')} XOF</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes & Conditions */}
              {(quote.notes || quote.conditions || settings?.conditions_paiement) && (
                <div className="p-8 bg-gray-50 border-t">
                  {quote.notes && (
                    <div className="mb-4">
                      <div className="text-sm font-semibold text-gray-700 mb-1">Notes:</div>
                      <p className="text-gray-600 text-sm whitespace-pre-wrap">{quote.notes}</p>
                    </div>
                  )}
                  {(quote.conditions || settings?.conditions_paiement) && (
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-1">Conditions:</div>
                      <p className="text-gray-600 text-sm whitespace-pre-wrap">
                        {quote.conditions || settings?.conditions_paiement}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="p-6 bg-indigo-600 text-white text-center">
                <p className="text-sm opacity-80">
                  {settings?.mentions_legales || 'Merci pour votre confiance !'}
                </p>
                {settings?.site_web && (
                  <p className="text-sm mt-1 opacity-60">{settings.site_web}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar - Actions */}
        <div className="space-y-6 print:hidden">
          {/* Actions Card */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Actions</h3>
            
            <div className="space-y-3">
              <button
                onClick={handlePrint}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
              >
                <Printer size={16} className="inline" /> Imprimer le devis
              </button>

              <Link
                to={`${routeBase}/quotes/${id}/edit`}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition"
              >
                <Edit size={16} className="inline" /> Modifier
              </Link>

              {quote.statut === 'brouillon' && (
                <button
                  onClick={() => handleStatusChange('envoye')}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition"
                >
                  <Send size={16} className="inline" /> Marquer comme envoyé
                </button>
              )}

              {quote.statut === 'envoye' && (
                <>
                  <button
                    onClick={() => handleStatusChange('accepte')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-100 text-green-700 rounded-xl hover:bg-green-200 transition"
                  >
                    <CheckCircle size={16} className="inline" /> Accepté par le client
                  </button>
                  <button
                    onClick={() => handleStatusChange('refuse')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition"
                  >
                    <XCircle size={16} className="inline" /> Refusé par le client
                  </button>
                </>
              )}

              {quote.statut === 'accepte' && (
                <button
                  onClick={handleConvertToInvoice}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition"
                >
                  <FileText size={14} className="inline" /> Convertir en facture
                </button>
              )}
            </div>
          </div>

          {/* Summary Card */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Résumé</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Client:</span>
                <span className="font-medium">{quote.client_nom}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Date:</span>
                <span className="font-medium">{new Date(quote.date_devis).toLocaleDateString('fr-FR')}</span>
              </div>
              {quote.date_validite && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Validité:</span>
                  <span className="font-medium">{new Date(quote.date_validite).toLocaleDateString('fr-FR')}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Nb articles:</span>
                <span className="font-medium">{quote.items?.length || 0}</span>
              </div>
              <hr />
              <div className="flex justify-between">
                <span className="font-semibold text-gray-700">Total:</span>
                <span className="font-bold text-indigo-600 text-lg">{total.toLocaleString('fr-FR')} XOF</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessQuoteView;
